/** A single gallery cell. In edit mode it's a `useSortable` item with a drag
 *  handle, a select checkbox, an edit affordance, a single-delete affordance, and
 *  a "Cover" chip when it's first in sequence. In view mode it's just a clickable
 *  thumbnail. Thumbnails load the THUMB preset so the grid never pulls originals.
 *  Images still downloading from a source URL show a spinner overlay. */
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { imageUrl, IMAGE_PRESET } from '@/utils/imageUrl';

import { isDownloading, type GalleryImage } from './types';

interface Props {
  image: GalleryImage;
  isCover: boolean;
  canManage: boolean;
  selected: boolean;
  onToggleSelect: (id: number) => void;
  onEdit: (image: GalleryImage) => void;
  onDelete: (image: GalleryImage) => void;
  onOpen: (image: GalleryImage) => void;
}

export function ImageCard({
  image,
  isCover,
  canManage,
  selected,
  onToggleSelect,
  onEdit,
  onDelete,
  onOpen,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image.id,
    disabled: !canManage,
  });

  const thumb = imageUrl(image.image, IMAGE_PRESET.THUMB);
  const downloading = isDownloading(image);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        // Square, as wide as its grid column (ImageGalleryGrid).
        width: '100%',
        aspectRatio: '1 / 1',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: 'action.hover',
      }}
    >
      {/* Thumbnail — click opens the lightbox (both view + edit modes). */}
      <Box
        role="button"
        tabIndex={0}
        aria-label={image.caption ? `Open image: ${image.caption}` : `Open image ${image.id}`}
        onClick={() => onOpen(image)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(image);
          }
        }}
        sx={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
      >
        {thumb ? (
          <Box component="img" src={thumb} alt={image.caption ?? ''} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'text.disabled', fontSize: 12 }}>
            No image
          </Box>
        )}
      </Box>

      {downloading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,0.35)' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {isCover && <Chip label="Cover" size="small" color="primary" sx={{ position: 'absolute', top: 6, left: 6 }} />}

      {image.is_360_degree && (
        <Tooltip title="360° panorama">
          <ThreeSixtyIcon sx={{ position: 'absolute', bottom: 6, left: 6, color: 'common.white', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))' }} />
        </Tooltip>
      )}

      {canManage && (
        <>
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelect(image.id)}
            inputProps={{ 'aria-label': `Select image ${image.id}` }}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              color: 'common.white',
              bgcolor: 'rgba(0,0,0,0.35)',
              p: 0.5,
              '&.Mui-checked': { color: 'primary.light' },
            }}
          />

          <Box sx={{ position: 'absolute', bottom: 4, right: 4, display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit details">
              <IconButton
                size="small"
                aria-label={`Edit image ${image.id}`}
                onClick={() => onEdit(image)}
                sx={{ color: 'common.white', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                aria-label={`Delete image ${image.id}`}
                onClick={() => onDelete(image)}
                sx={{ color: 'common.white', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Drag handle — dnd-kit listeners live here so buttons/thumb stay clickable. */}
          <Tooltip title="Drag to reorder">
            <IconButton
              size="small"
              aria-label={`Reorder image ${image.id}`}
              {...attributes}
              {...listeners}
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                color: 'common.white',
                bgcolor: 'rgba(0,0,0,0.45)',
                cursor: 'grab',
                touchAction: 'none',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
}
